import type { PlainMessage } from '@bufbuild/protobuf';
import type { IntlShape } from 'react-intl';

import type {
  NoteTeethGroup,
  NoteToothMark,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import {
  DocumentAnswerSource,
  NoteAIBasis,
  NoteEvidenceSource,
  NoteLineStatus,
  type DocumentAnswer,
  type DocumentAnswers as NoteAnswers,
  type NoteBlockView,
  type NoteLineView,
  type NoteToothNotation,
} from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

import {
  noteChoicePrint,
  noteTeethHaveMarks,
  type DocumentTemplate,
} from '@/entities/templates';

import type { PendingAnswer } from '@/features/audioRecording';

import type { NoteEditorViewFilter } from './blockEditorView';
import {
  EMPTY_LINE_PRESETS,
  getPresetShownLineIds,
  orderPresetSectionLines,
  type BlockPresets,
  type LinePresets,
  type PresetAddedLine,
} from './blockPresets';
import { startsWithDiagnosisCode } from './diagnosisCode';
import { printNoteDate } from './noteDate';
import { printNoteNumbers } from './noteNumbers';
import {
  listNoteTableAiCells,
  noteTableCellKey,
  printNoteTable,
  type NoteTableAiCell,
} from './noteTable';
import { printNoteTeethMarks } from './noteTeethMarks';
import {
  describeNoteLine,
  nameNoteBlock,
  nameNoteLine,
  findNoteAnswer,
  findNoteOwnOptions,
  findPendingNoteChange,
  getNoteSectionTitle,
  indexNoteBlocks,
  indexNoteLines,
  isAiRemovedNoteLine,
  isNoteLineSetAside,
  isNoteLineShown,
  isWaitingAiHint,
  listUnresolvedNoteLines,
  printNoteChoice,
  printNoteNone,
  printNoteTeeth,
  readNoteBlockSelection,
  stripInlineGuidance,
  type NoteBlockDef,
  type NoteBlockSelection,
  type NoteLineDef,
  type UnresolvedNoteLine,
} from './noteView';

// One surface shows a template and its note in every state: an option
// template with its working copy's answers, a block template with the values
// set before the recording, and a block note from scribe's NoteView. These
// builders read either source into the same model; they never decide what a
// note shows, which is scribe's once the encounter exists.

export type SurfaceSource = 'ai' | 'manual' | null;

// What an AI value rests on: said, said in other words, a dictated option the
// template lacks, assumed without being said, or suggested for the case by
// "Complete with AI".
export type SurfaceAiBasis =
  | 'stated'
  | 'matched'
  | 'newOption'
  | 'assumed'
  | 'suggested';

// How many lines of a section are filled, for the document's progress.
export type SurfaceSectionHead = {
  filled: number;
  total: number;
};

export type SurfaceTextSpan = { text: string; ai: boolean };

export type SurfaceBlock = {
  id: string;
  // Missing only for a block neither the template nor the note knows.
  def: NoteBlockDef | undefined;
  // What the note prints for the block; before the recording, the template
  // wording or the values set.
  text: string;
  // The runs of fixed wording an after-Stop AI edit wrote.
  spans: SurfaceTextSpan[];
  resolved: boolean;
  source: SurfaceSource;
  // Null for a manual value and for an AI value stored before scribe marked
  // its basis, which shows as said.
  basis: SurfaceAiBasis | null;
  // The words the clinician said for a matched value.
  said: string;
  // Why a suggested value fits the case, in the note's language.
  rationale: string;
  // The words of the transcript an AI value was read from.
  quote: string;
  addedByAi: boolean;
  correction: string | undefined;
  selection: NoteBlockSelection;
  // A teeth field that stores marks: the teeth whose marks AI set.
  aiMarkedTeeth?: string[];
  // A teeth field that stores marks: the marks AI assumed or suggested,
  // each waiting for the clinician, with the words it was read from.
  unconfirmedMarks?: { mark: PlainMessage<NoteToothMark>; quote: string }[];
  // A table field: the cells AI set, by noteTableCellKey.
  aiCells?: Record<string, NoteTableAiCell>;
  // An AI value the clinician's instruction to "Complete with AI" holds.
  fromInstruction?: boolean;
  // The options "Other" added to an option block in this note, in the order
  // added; only a chosen one prints.
  ownOptions: string[];
  // The stored answer is the clinician's and displaced an AI one.
  canRevert: boolean;
  // An AI hint the clinician confirmed: an ordinary AI value from then on,
  // whose basis the note no longer shows.
  aiConfirmed?: boolean;
  // The clinician changed it and the server has not answered yet.
  pending: boolean;
  // What a partner template copies for the block while it is empty: a hint,
  // never a value.
  partnerDefault: string;
};

export type SurfaceLineState = 'included' | 'unresolved' | 'aiRemoved';

export type SurfaceLine = {
  id: string;
  def: NoteLineDef | undefined;
  state: SurfaceLineState;
  // Before the recording: a linked line the values set do not reveal.
  dormant: boolean;
  revealedAt: bigint;
  addedByAi: boolean;
  note: string;
  // The printed line, or a locator built from the template wording.
  text: string;
  blocks: SurfaceBlock[];
  // A partner template prints the line while its teeth picker is empty.
  teethMissing: boolean;
};

export type SurfaceSetAsideLine = {
  id: string;
  text: string;
  skipped: boolean;
};

export type SurfaceSection = {
  id: string;
  code: string;
  title: string;
  head: SurfaceSectionHead;
  lines: SurfaceLine[];
  // Every line the note holds in the section, shown or not.
  noteLineIds: string[];
  setAside: SurfaceSetAsideLine[];
  // Linked lines the view filter keeps out of sight.
  hiddenLinked: number;
  unresolvedLineIds: string[];
  missingRequired: boolean;
  customOrder: boolean;
  // The section as scribe rendered it, for copying.
  text: string;
};

// An AI value nobody said, which holds the note until the clinician
// confirms, changes or clears it.
export type NoteAssumption = {
  sectionId: string;
  sectionTitle: string;
  lineId: string;
  lineText: string;
  blockId: string;
  selection: NoteBlockSelection;
  // The value as printed and what it rests on.
  value: string;
  quote: string;
  said: string;
  // A diagnosis code is confirmed on its own, never with "Confirm all".
  coded: boolean;
  // A part of the value that waits on its own (a tooth's mark, a table's
  // cell) inside a value that does not: its key in the block. Confirming it
  // sets the field as it is, which confirms every part the field keeps.
  part?: string;
};

export type NoteSurface = {
  sections: SurfaceSection[];
  progress: { filled: number; total: number };
  ready: boolean;
  // The unresolved lines that hold "Save to record": none on a partner
  // template's note, whose open lines keep the template's defaults.
  unresolved: UnresolvedNoteLine[];
  assumptions: NoteAssumption[];
};

const EMPTY_SELECTION: NoteBlockSelection = {
  values: [],
  other: '',
  teeth: [],
  groups: [],
  date: '',
};

const toSource = (source: DocumentAnswerSource | undefined): SurfaceSource => {
  switch (source) {
    case DocumentAnswerSource.DocumentAnswerSourceAI:
      return 'ai';
    case DocumentAnswerSource.DocumentAnswerSourceManual:
      return 'manual';
    default:
      return null;
  }
};

const toBasis = (basis: NoteAIBasis | undefined): SurfaceAiBasis | null => {
  switch (basis) {
    case NoteAIBasis.NoteAIBasisStated:
      return 'stated';
    case NoteAIBasis.NoteAIBasisMatched:
      return 'matched';
    case NoteAIBasis.NoteAIBasisNewOption:
      return 'newOption';
    case NoteAIBasis.NoteAIBasisAssumed:
      return 'assumed';
    case NoteAIBasis.NoteAIBasisSuggested:
      return 'suggested';
    default:
      return null;
  }
};

// A value AI assumed or suggested waits for the clinician's confirmation; an
// unsent change of the block settles it either way.
export const isUnconfirmedAiValue = (block: SurfaceBlock) =>
  block.source === 'ai' &&
  (block.basis === 'assumed' || block.basis === 'suggested') &&
  !block.pending;

// What an AI value rests on, when it is more than said as it is.
export const describeAiBasis = (
  formatMessage: IntlShape['formatMessage'],
  {
    basis,
    said,
    rationale,
    aiConfirmed,
  }: Pick<SurfaceBlock, 'basis' | 'said' | 'rationale' | 'aiConfirmed'>,
) => {
  if (aiConfirmed) {
    return formatMessage({
      id: 'workspace.note.aiConfirmedHint',
      defaultMessage: 'AI proposed this value; the clinician confirmed it.',
    });
  }
  switch (basis) {
    case 'assumed':
      return formatMessage({
        id: 'workspace.note.aiAssumedHint',
        defaultMessage:
          'AI assumed this value: nobody said it. Confirm it or clear it.',
      });
    case 'newOption':
      return formatMessage({
        id: 'workspace.note.aiNewOptionHint',
        defaultMessage:
          'AI added this option from what was said: the template does not have it.',
      });
    case 'matched':
      return said
        ? formatMessage(
            {
              id: 'workspace.note.aiMatchedHint',
              defaultMessage:
                'AI chose this value for the words said: “{said}”.',
            },
            { said },
          )
        : undefined;
    case 'suggested':
      return rationale
        ? formatMessage(
            {
              id: 'workspace.note.aiSuggestedHint',
              defaultMessage:
                'AI suggested this value for the case: {rationale}',
            },
            { rationale },
          )
        : undefined;
    default:
      return undefined;
  }
};

const holdsDiagnosisCode = ({ text, selection }: SurfaceBlock) =>
  [text, ...selection.values, selection.other].some(startsWithDiagnosisCode);

type NotePart = { part: string; value: string; quote: string };

// The parts of a block's value that wait for the clinician on their own:
// the marks and the table cells AI assumed or suggested, each as the note
// prints it.
const listUnconfirmedParts = (
  block: SurfaceBlock,
  printing: { language?: string; notation?: NoteToothNotation },
): NotePart[] => {
  const kind = block.def?.Kind;
  if (kind?.case === 'Teeth') {
    return (block.unconfirmedMarks ?? []).map(({ mark, quote }) => ({
      part: `mark:${mark.Tooth}:${mark.Surface ?? ''}`,
      value: printNoteTeethMarks(kind.value, [mark], printing),
      quote,
    }));
  }
  if (kind?.case !== 'Table') {
    return [];
  }
  return (block.selection.tableRows ?? []).flatMap((row) =>
    row.Cells.flatMap((cell) => {
      const key = noteTableCellKey(row.Row, cell.Column);
      const ai = block.aiCells?.[key];
      return ai?.unconfirmed
        ? [
            {
              part: `cell:${key}`,
              value: printNoteTable(
                kind.value,
                [{ Row: row.Row, Cells: [cell] }],
                printing,
              ),
              quote: ai.quote,
            },
          ]
        : [];
    }),
  );
};

// What "Confirm all" takes: every assumption but a diagnosis code.
export const listBatchConfirmable = (assumptions: NoteAssumption[]) =>
  assumptions.filter((assumption) => !assumption.coded);

// Blocks that take a value; fixed wording and record values do not.
export const isNoteValueBlock = (block: NoteBlockDef | undefined) =>
  !!block && block.Kind.case !== 'Text' && block.Kind.case !== 'Context';

// A line of fixed wording only holds no value box, so it takes a text line.
export const isTextOnlyLine = (line: SurfaceLine) =>
  line.blocks.every((block) => block.def?.Kind.case === 'Text');

const hasSelection = ({
  values,
  other,
  teeth,
  groups,
  date,
  marks,
  none,
  numbers,
  tableRows,
}: NoteBlockSelection) =>
  values.length > 0 ||
  !!other ||
  teeth.length > 0 ||
  groups.length > 0 ||
  !!date ||
  !!marks?.length ||
  !!none ||
  !!numbers?.length ||
  !!tableRows?.length;

// What the line reset clears: the value blocks that hold a value, stored or
// still being sent.
export const isFilledValueBlock = (block: SurfaceBlock) =>
  isNoteValueBlock(block.def) &&
  (block.resolved || hasSelection(block.selection));

// A text block whose wording the clinician removed: it prints nothing.
export const isRemovedNoteWording = (block: SurfaceBlock) =>
  block.def?.Kind.case === 'Text' && block.source === 'manual' && !block.text;

// What a line's reset takes back: the value blocks it clears and the removed
// wordings it releases.
export type NoteLineResetParts = { values: string[]; wordings: string[] };

export const listNoteLineResetParts = (
  line: SurfaceLine,
): NoteLineResetParts => ({
  values: line.blocks.filter(isFilledValueBlock).map((block) => block.id),
  wordings: line.blocks.filter(isRemovedNoteWording).map((block) => block.id),
});

const sumProgress = (heads: SurfaceSectionHead[]) =>
  heads.reduce(
    (progress, head) => ({
      filled: progress.filled + head.filled,
      total: progress.total + head.total,
    }),
    { filled: 0, total: 0 },
  );

// How a value set before the recording prints: as scribe's render prints it,
// with the teeth groups named in the template language.
type PresetPrinting = {
  teethGroupName: (group: NoteTeethGroup) => string;
  language: string;
};

const printPresetValue = (
  block: NoteBlockDef,
  selection: NoteBlockSelection,
  { teethGroupName, language }: PresetPrinting,
) => {
  if (selection.none) {
    return printNoteNone(block.Kind.value as { NoneLabel?: string }, language);
  }
  switch (block.Kind.case) {
    case 'Teeth':
      if (noteTeethHaveMarks(block.Kind.value)) {
        return printNoteTeethMarks(block.Kind.value, selection.marks ?? [], {
          language,
        });
      }
      return printNoteTeeth({
        teeth: selection.teeth,
        groups: selection.groups,
        notation: undefined,
        groupName: teethGroupName,
      });
    case 'Date':
      return printNoteDate(selection.date, language);
    case 'Number':
      return printNoteNumbers(
        selection.numbers ?? [],
        block.Kind.value.Unit,
        language,
      );
    case 'Table':
      return printNoteTable(block.Kind.value, selection.tableRows ?? [], {
        language,
      });
    default:
      return printNoteChoice({
        values: selection.values.map((value) => noteChoicePrint(block, value)),
        other: selection.other,
      });
  }
};

const presetBlock = (
  block: NoteBlockDef,
  presets: BlockPresets,
  printing: PresetPrinting,
): SurfaceBlock => {
  const base = {
    id: block.ID,
    def: block,
    spans: [],
    basis: null,
    said: '',
    rationale: '',
    quote: '',
    addedByAi: false,
    correction: undefined,
    canRevert: false,
    pending: false,
    partnerDefault: '',
  };
  const preset = presets[block.ID];
  if (!isNoteValueBlock(block)) {
    const wording = block.Kind.case === 'Text' ? block.Kind.value.Value : '';
    const edited =
      block.Kind.case === 'Text' && (!!preset?.text || !!preset?.removed);
    return {
      ...base,
      text: edited ? preset.text : wording,
      resolved: true,
      source: edited ? 'manual' : null,
      selection: EMPTY_SELECTION,
      ownOptions: [],
    };
  }
  const selection = preset
    ? {
        values: preset.values,
        other: preset.text,
        teeth: preset.teeth,
        groups: preset.teethGroups,
        date: preset.date ?? '',
        marks: preset.marks,
        none: preset.none,
        numbers: preset.numbers,
        tableRows: preset.tableRows,
      }
    : EMPTY_SELECTION;
  const chosen = hasSelection(selection);
  return {
    ...base,
    text: chosen ? printPresetValue(block, selection, printing) : '',
    resolved: chosen,
    source: chosen ? 'manual' : null,
    selection,
    ownOptions: preset?.ownOptions ?? [],
  };
};

// A line added before the recording is one text block, as scribe adds it.
const presetAddedLine = (
  added: PresetAddedLine,
  printing: PresetPrinting,
): SurfaceLine => {
  const block: NoteBlockDef = {
    ID: added.blockId,
    SlotID: '',
    Kind: { case: 'Text', value: { Value: added.text } },
  };
  return {
    id: added.id,
    def: { ID: added.id, Blocks: [block], ShowWhen: [] },
    state: 'included',
    dormant: false,
    revealedAt: BigInt(0),
    addedByAi: false,
    note: '',
    text: added.text,
    blocks: [presetBlock(block, {}, printing)],
    teethMissing: false,
  };
};

// The note render puts a space between blocks and none before punctuation.
export const startsWithPunctuation = ({ def, text }: SurfaceBlock) =>
  def?.Kind.case === 'Text' && /^[.,;:%)]/.test(text);

// A line set aside prints as the note would: its wording and values, with a
// gap for a value not set yet.
const printPresetLine = (line: SurfaceLine) =>
  line.blocks
    .map((block) => ({
      block,
      text:
        block.def?.Kind.case === 'Text'
          ? stripInlineGuidance(block.text).trim()
          : block.text || (block.def ? nameNoteBlock(block.def) : '…'),
    }))
    .filter(({ text }) => text)
    .map(({ block, text }, index) =>
      index > 0 && !startsWithPunctuation(block) ? ` ${text}` : text,
    )
    .join('');

// Before the recording there is no encounter: the template is laid out with
// the values and line operations set on it, and a linked line is revealed by
// the rule scribe applies, as a display hint only.
export const buildPresetNoteSurface = ({
  template,
  presets,
  lines = EMPTY_LINE_PRESETS,
  filter = 'all',
  teethGroupName,
}: {
  template: DocumentTemplate;
  presets: BlockPresets;
  lines?: LinePresets;
  filter?: NoteEditorViewFilter;
  teethGroupName: PresetPrinting['teethGroupName'];
}): NoteSurface => {
  const printing = { teethGroupName, language: template.language };
  const shown = new Set(
    getPresetShownLineIds(template, presets, lines.deleted),
  );
  const sections = template.sections.map((section): SurfaceSection => {
    const byId = new Map(
      [
        ...section.Lines.map(
          (line): SurfaceLine => ({
            id: line.ID,
            def: line,
            state: 'included',
            dormant: !shown.has(line.ID),
            revealedAt: BigInt(0),
            addedByAi: false,
            note: '',
            text: describeNoteLine(line),
            blocks: line.Blocks.map((block) =>
              presetBlock(block, presets, printing),
            ),
            teethMissing: false,
          }),
        ),
        ...lines.added
          .filter((added) => added.sectionId === section.ID)
          .map((added) => presetAddedLine(added, printing)),
      ].map((line) => [line.id, line]),
    );
    const ordered = orderPresetSectionLines(section, lines).flatMap(
      (id) => byId.get(id) ?? [],
    );
    const kept = ordered.filter((line) => !lines.deleted.includes(line.id));
    // Lines to fill are the revealed ones that take a value.
    const valueLines = kept.filter(
      (line) =>
        !line.dormant &&
        line.blocks.some((block) => isNoteValueBlock(block.def)),
    );
    const visible =
      filter === 'hideLinked' ? kept.filter((line) => !line.dormant) : kept;
    return {
      id: section.ID,
      code: section.Code,
      title: section.Title,
      head: {
        filled: valueLines.filter((line) =>
          line.blocks.every(
            (block) => !isNoteValueBlock(block.def) || block.resolved,
          ),
        ).length,
        total: valueLines.length,
      },
      lines: visible,
      noteLineIds: ordered.map((line) => line.id),
      setAside: ordered
        .filter((line) => lines.deleted.includes(line.id))
        .map((line) => ({
          id: line.id,
          text: printPresetLine(line),
          skipped: false,
        })),
      hiddenLinked: filter === 'hideLinked' ? kept.length - visible.length : 0,
      unresolvedLineIds: [],
      missingRequired: false,
      customOrder: !!lines.orders[section.ID],
      text: '',
    };
  });
  return {
    sections,
    progress: sumProgress(sections.map((section) => section.head)),
    ready: false,
    unresolved: [],
    assumptions: [],
  };
};

const viewBlock = ({
  view,
  def,
  answer,
  ownOptions,
  pending,
}: {
  view: NoteBlockView;
  def: NoteBlockDef | undefined;
  answer: DocumentAnswer | undefined;
  ownOptions: string[];
  pending: Record<string, PendingAnswer>;
}): SurfaceBlock => {
  const change = findPendingNoteChange(pending, view.BlockID);
  const source = toSource(view.Source);
  const aiConfirmed =
    source === 'ai' && (view.AIConfirmed || !!answer?.AIConfirmed);
  return {
    id: view.BlockID,
    def,
    text: view.Text,
    spans: view.TextSpans.map(({ Text, AI }) => ({ text: Text, ai: AI })),
    resolved: view.Resolved,
    source,
    basis: aiConfirmed ? null : toBasis(view.AIBasis),
    aiConfirmed,
    said: view.AISaid || (source === 'ai' ? answer?.AISaid ?? '' : ''),
    rationale: view.AIRationale,
    quote: source === 'ai' ? answer?.EvidenceQuote ?? '' : '',
    addedByAi: toSource(view.AddedBy) === 'ai',
    correction: view.CorrectionText,
    selection: readNoteBlockSelection(answer, change),
    fromInstruction:
      source === 'ai' &&
      (view.EvidenceSource ?? answer?.EvidenceSource) ===
        NoteEvidenceSource.NoteEvidenceSourceInstruction,
    aiMarkedTeeth: (answer?.Marks ?? []).flatMap(({ Mark, Source }) =>
      Mark && Source === DocumentAnswerSource.DocumentAnswerSourceAI
        ? [Mark.Tooth]
        : [],
    ),
    unconfirmedMarks: (answer?.Marks ?? []).flatMap(
      ({ Mark, Source, EvidenceQuote, ...hint }) =>
        Mark &&
        Source === DocumentAnswerSource.DocumentAnswerSourceAI &&
        isWaitingAiHint(hint)
          ? [{ mark: Mark, quote: EvidenceQuote }]
          : [],
    ),
    aiCells: listNoteTableAiCells(answer?.TableRows ?? []),
    ownOptions,
    canRevert:
      answer?.Source === DocumentAnswerSource.DocumentAnswerSourceManual &&
      answer.CanRevert,
    pending: !!change,
    partnerDefault: view.PartnerDefaultText,
  };
};

const lineState = (line: NoteLineView): SurfaceLineState => {
  if (isAiRemovedNoteLine(line)) {
    return 'aiRemoved';
  }
  return line.Status === NoteLineStatus.NoteLineStatusUnresolved
    ? 'unresolved'
    : 'included';
};

// The note during and after the recording, laid out from scribe's view:
// which lines are shown, their status, every printed text and the chosen
// values all come from the server; the clinician's unsent changes show
// until it answers.
export const buildViewNoteSurface = ({
  template,
  answers,
  pending,
}: {
  template: DocumentTemplate;
  answers: NoteAnswers;
  pending: Record<string, PendingAnswer>;
}): NoteSurface => {
  const view = answers.View;
  const partnerDefaults = !!view?.PartnerDefaults;
  const printing = {
    language: template.language,
    notation: answers.Context?.Notation,
  };
  const blocks = indexNoteBlocks(template, answers);
  const lines = indexNoteLines(template, answers);
  const sections = (view?.Sections ?? []).map((section): SurfaceSection => {
    const placed = section.Lines.filter(
      (line) => isNoteLineShown(line) || isAiRemovedNoteLine(line),
    ).map(
      (line): SurfaceLine => ({
        id: line.LineID,
        def: lines.get(line.LineID)?.line,
        state: lineState(line),
        dormant: false,
        revealedAt: line.RevealedAtRevision,
        addedByAi: toSource(line.AddedBy) === 'ai',
        note: line.Note ?? '',
        text: line.Text || describeNoteLine(lines.get(line.LineID)?.line),
        blocks: line.Blocks.map((block) =>
          viewBlock({
            view: block,
            def: blocks.get(block.BlockID),
            answer: findNoteAnswer(answers, block.BlockID),
            ownOptions: findNoteOwnOptions(answers, block.BlockID),
            pending,
          }),
        ),
        teethMissing: line.PartnerTeethMissing,
      }),
    );
    const shown = placed.filter((line) => line.state !== 'aiRemoved');
    const unresolved = shown.filter((line) => line.state === 'unresolved');
    return {
      id: section.SectionID,
      code:
        section.SectionCode ||
        (template.sections.find((item) => item.ID === section.SectionID)
          ?.Code ??
          ''),
      title: getNoteSectionTitle(template, section.SectionID),
      head: {
        filled: shown.length - unresolved.length,
        total: shown.length,
      },
      lines: placed,
      noteLineIds: section.Lines.map((line) => line.LineID),
      setAside: section.Lines.filter(isNoteLineSetAside).map((line) => ({
        id: line.LineID,
        text: line.Text || nameNoteLine(lines.get(line.LineID)?.line),
        skipped: line.Status === NoteLineStatus.NoteLineStatusSkipped,
      })),
      hiddenLinked: 0,
      unresolvedLineIds: unresolved.map((line) => line.id),
      missingRequired: section.MissingRequired,
      customOrder: section.CustomLineOrder,
      text: section.Text,
    };
  });
  return {
    sections,
    progress: sumProgress(sections.map((section) => section.head)),
    ready: !!view?.ReadyToFinish,
    unresolved: partnerDefaults
      ? []
      : listUnresolvedNoteLines(template, answers),
    assumptions: sections.flatMap((section) =>
      section.lines
        .filter((line) => line.state !== 'aiRemoved')
        .flatMap((line) =>
          line.blocks.flatMap((block): NoteAssumption[] => {
            const base = {
              sectionId: section.id,
              sectionTitle: section.title,
              lineId: line.id,
              lineText: line.text,
              blockId: block.id,
              selection: block.selection,
            };
            if (isUnconfirmedAiValue(block)) {
              return [
                {
                  ...base,
                  value: block.text,
                  quote: block.quote,
                  said: block.said,
                  coded: holdsDiagnosisCode(block),
                },
              ];
            }
            return block.pending
              ? []
              : listUnconfirmedParts(block, printing).map(
                  ({ part, value, quote }) => ({
                    ...base,
                    value,
                    quote,
                    said: '',
                    coded: false,
                    part,
                  }),
                );
          }),
        ),
    ),
  };
};
